import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Alert,
  SelectChangeEvent,
  Button,
  Stack,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@store/index';
import { selectTheme, setTheme } from '@features/ui/uiSlice';
import { selectUser } from '@features/auth/authSlice';

const UserSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelector(selectTheme);
  const user = useAppSelector(selectUser);

  // Derive the role set the same way AuditDashboardPage does — both a
  // singular ``role`` string and a ``roles`` array may be populated,
  // depending on how the JWT was issued. This keeps the gating logic
  // identical to the audit page itself, so users who see the link can
  // actually navigate to the target (no dead buttons).
  const userRoles = React.useMemo(() => {
    const roles: string[] = [];
    if (user?.role) roles.push(user.role);
    if (Array.isArray(user?.roles)) {
      user.roles.forEach((r) => {
        if (!roles.includes(r)) roles.push(r);
      });
    }
    return roles;
  }, [user]);
  const canViewActivity = userRoles.includes('admin') || userRoles.includes('manager');
  const canViewAnomalies = userRoles.includes('admin');
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [emailNotifications, setEmailNotifications] = React.useState(() => {
    const saved = localStorage.getItem('emailNotifications');
    return saved !== null ? saved === 'true' : false;
  });

  const handleThemeChange = (event: SelectChangeEvent) => {
    dispatch(setTheme(event.target.value as 'light' | 'dark'));
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <SettingsIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h4">{t('settings.title')}</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('settings.description')}
      </Typography>

      {/* Appearance Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <PaletteIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('settings.appearance')}</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="theme-label">{t('settings.theme')}</InputLabel>
            <Select
              labelId="theme-label"
              value={currentTheme}
              label={t('settings.theme')}
              onChange={handleThemeChange}
            >
              <MenuItem value="light">{t('settings.themeLight')}</MenuItem>
              <MenuItem value="dark">{t('settings.themeDark')}</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <LanguageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('settings.language')}</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <FormControl fullWidth>
            <InputLabel id="language-label">{t('settings.selectLanguage')}</InputLabel>
            <Select
              labelId="language-label"
              value={i18n.language?.split('-')[0] || 'ja'}
              label={t('settings.selectLanguage')}
              onChange={handleLanguageChange}
            >
              <MenuItem value="ja">{t('settings.languageOptions.japanese')}</MenuItem>
              <MenuItem value="en">{t('settings.languageOptions.english')}</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <NotificationsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('settings.notifications')}</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={notificationsEnabled}
                onChange={(e) => {
                  const value = e.target.checked;
                  setNotificationsEnabled(value);
                  localStorage.setItem('notificationsEnabled', String(value));
                }}
              />
            }
            label={t('settings.enableNotifications')}
            sx={{ mb: 2, display: 'block' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={emailNotifications}
                onChange={(e) => {
                  const value = e.target.checked;
                  setEmailNotifications(value);
                  localStorage.setItem('emailNotifications', String(value));
                }}
                disabled={!notificationsEnabled}
              />
            }
            label={t('settings.emailNotifications')}
            sx={{ display: 'block' }}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            {t('settings.notificationNote')}
          </Alert>
        </CardContent>
      </Card>

      {/*
        Activity / Audit entry point.
        Only rendered for manager / admin — the audit dashboard and the
        anomaly triage view both 403 for end users, so showing the link
        would be a dead button. ``canViewActivity`` and
        ``canViewAnomalies`` mirror the role check in
        ``AuditDashboardPage`` so the guarded page always loads cleanly.
      */}
      {canViewActivity && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <AssessmentIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                {t('settings.activityTitle')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('settings.activityDescription')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate('/admin/activity')}
              >
                {t('settings.openActivityDashboard')}
              </Button>
              {canViewAnomalies && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ReportProblemIcon />}
                  onClick={() => navigate('/admin/anomalies')}
                >
                  {t('settings.openAnomalyTriage')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default UserSettingsPage;
